import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Download } from 'lucide-react'
import { stripPointerSuffix, isPointerRow as isLivePointerRow, isChainPointerRow } from '@papan/types'

/**
 * The fields this dialog actually reads — not `FileItem`.
 *
 * Three surfaces offer a pointer download (the drive, the mobile sheet, the
 * Shared page) and only one of them has `FileItem` rows. Requiring the full
 * shape is why the other two ended up calling `downloadItem` directly and
 * silently losing the choice, and with it the dead-pointer case: a revoked
 * share downloaded as a near-empty zip instead of the JSON that can rebuild
 * it. `FileItem` satisfies this structurally, so the drive is unchanged.
 */
export interface PointerRowLike {
  id: string
  name: string
  isFolder: boolean
  /** DB row kind — 'shared' / 'chain' for a real mount artifact. */
  fileType?: string | null
  shareBroken?: boolean
  /** Refines `shareBroken`'s label for a never-granted import. Not read here —
   *  the listing only ever emits it ALONGSIDE `shareBroken` — but carried so a
   *  real row assigns cleanly. */
  shareNoAccess?: boolean
  chainBroken?: boolean
  /** Target's name for a `.chain`; a pointer suffix here means it points at
   *  another pointer, which cannot be walked through. */
  chainTargetName?: string | null
}

/** Alias kept so existing call sites read unchanged. */
type FileItem = PointerRowLike

/** A LIVE pointer — kind AND suffix, the drive listing's one classifier (I110). */
function isPointerRow(file: FileItem): boolean {
  return isLivePointerRow(file)
}

/**
 * What a Download on this row should DO, so three surfaces cannot disagree.
 *
 * The drive derived this inline and the other two never derived it at all.
 */
export type PointerDownloadRoute = 'metadata' | 'choose' | 'plain'

export function routePointerDownload(file: PointerRowLike): PointerDownloadRoute {
  if (!isPointerRow(file)) return 'plain'
  // Nothing to choose between: the JSON is the whole download, and asking
  // would be a question with one answer.
  if (pointerHasNoContents(file)) return 'metadata'
  return 'choose'
}

/**
 * A dead pointer has no source content to offer — its grant is revoked, was
 * never granted (`shareNoAccess`, which the listing endpoint only ever emits
 * ALONGSIDE `shareBroken`), or its same-tenant target no longer resolves.
 *
 * A single one skips the dialog and downloads the pointer JSON straight: it is
 * the only thing there is, and it is exactly what the upload-aligner round-trip
 * needs to recreate the share elsewhere.
 *
 * This is a coarse signal by design. A LIVE pointer whose grant is read-only
 * or fully excluded also has little or nothing downloadable, but the server
 * only decides that when it resolves the source — a folder yields a zip of
 * whatever is permitted, a file refuses with 403. The listing carries no flag
 * for either, so those get the dialog and then whatever the server gives.
 */
export function pointerHasNoContents(file: FileItem): boolean {
  if (file.shareBroken || file.chainBroken) return true
  // A `.chain` may legally target a folder-target `.shared` — the create route
  // says so ("Chaining a `.shared` IS still supported: a local handle to inbound
  // cross-tenant content is a real gesture"). The download walk lists that
  // pointer row's NATIVE children, of which there are none, so it yields a zip
  // holding one empty directory entry — 120 bytes where the `.shared` itself
  // downloads 20,966. Resolving THROUGH would mean re-deriving the resolver's
  // min-stack inside the download route (the chain's own placement can cap the
  // share's grant, and dropping that cap is a permission bug), so we stop
  // PROMISING contents rather than pretend to deliver them. The extension is
  // the authoritative switch here, as it is everywhere else pointers are typed.
  if (isChainPointerRow(file) && /\.(?:shared|chain)$/i.test(file.chainTargetName ?? '')) return true
  return false
}

/** What `stripPointerSuffix(name)` plus the target kind will actually save. */
function producesName(file: FileItem): string {
  if (!isPointerRow(file)) return file.isFolder ? `${file.name}.zip` : file.name
  // A dead pointer's whole download IS its share file, under its own name.
  if (pointerHasNoContents(file)) return file.name
  const base = stripPointerSuffix(file.name)
  return file.isFolder ? `${base}.zip` : base
}

export interface PointerDownloadPlan {
  /** Ordinary rows — one server request, zipped together by the server. */
  plain: FileItem[]
  /** Pointers whose source resolves: contents, or their own file instead. */
  live: FileItem[]
  /** Pointers whose contents are unreachable — a dead grant, or a chain over
   *  another pointer. Their own JSON is the whole download, so it rides
   *  whichever option is chosen. */
  shareFileOnly: FileItem[]
  /** What the ONE archive lands as for a multi-row selection, or `null` for a
   *  single row — there the radios already name both candidate files. */
  archiveName: string | null
  /** Live pointers — the ones the second radio applies to. */
  shareFileCount: number
  producesFor: (file: FileItem) => string
}

/**
 * Split a selection into the downloads it will actually produce.
 *
 * The server's pointer branch is single-id by construction — it resolves ONE
 * mount — so a selection mixing pointers with ordinary rows cannot be one
 * request. Left to the plain multi-item path those ids reach the native walk,
 * which has no blob to read for a blob-less pointer row and drops them into the
 * zip's `_SKIPPED_FILES.txt` under copy written for unreadable blobs ("retry
 * the download — the source may have been mid-sync"), advice that can never
 * work here. So the selection is executed as several downloads, and this is the
 * ONE description of what those are — shared by the dialog that previews them
 * and the handler that runs them, so the preview cannot promise a different
 * set from the one that lands.
 */
export function planPointerDownload(files: FileItem[]): PointerDownloadPlan {
  const plain = files.filter((f) => !isPointerRow(f))
  const pointers = files.filter(isPointerRow)
  const live = pointers.filter((f) => !pointerHasNoContents(f))
  const shareFileOnly = pointers.filter(pointerHasNoContents)

  // Mirrors the server: several rows land as `files (N items).zip`. A single row
  // is named by the radios instead, because which file it produces depends on
  // the choice. Naming it differently here would preview a file the user never
  // receives.
  const archiveName = files.length > 1 ? `files (${files.length} items).zip` : null

  return {
    plain,
    live,
    shareFileOnly,
    archiveName,
    // share-file-only pointers are excluded: theirs rides either way, so the
    // choice does not apply to them.
    shareFileCount: live.length,
    producesFor: producesName,
  }
}

/** `'contents'` — the source content of each live pointer.
 *  `'pointer'`  — each live pointer's own `.shared`/`.chain` file instead. */
export type PointerDownloadChoice = 'contents' | 'pointer'

/**
 * A selectable option card — the app's own control, not the OS radio.
 *
 * Same shape `IdentityRequiredDialog` uses for picking an identity: a bordered
 * card that carries the selected state, a title, and a muted line saying what
 * the option actually gives you. The dot is drawn here so it inherits the
 * theme; the native input stays, visually hidden, so keyboard navigation,
 * focus and assistive tech keep working — `accent-*` on a visible input would
 * be painting the browser default rather than replacing it.
 *
 * The focus ring is gated on `:focus-visible`, NOT `focus-within`. Clicking a
 * label focuses the input it wraps, so `focus-within` drew a blue rectangle on
 * every mouse click — indistinguishable from a border, and exactly the thing a
 * ring is not for. `has-[:focus-visible]` leaves the mouse path clean and still
 * shows the ring when the card is reached by keyboard.
 */
function ChoiceCard({
  name, value, checked, onSelect, disabled, title, description,
}: {
  name: string
  value: string
  checked: boolean
  onSelect: () => void
  disabled?: boolean
  title: string
  description: string
}) {
  return (
    <label
      className={`flex items-start gap-2.5 px-3 py-2.5 rounded-md cursor-pointer transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-500 ${
        checked ? 'themed-bg-tertiary' : 'hover:themed-bg-tertiary'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onSelect}
        disabled={disabled}
        className="sr-only"
      />
      <span
        aria-hidden
        className={`mt-0.5 shrink-0 h-4 w-4 rounded-full border-2 flex items-center justify-center transition-colors ${
          checked ? 'border-blue-500' : 'themed-border-secondary'
        }`}
      >
        {checked && <span className="h-2 w-2 rounded-full bg-blue-500" />}
      </span>
      <span className="min-w-0">
        <span className="block text-sm themed-text truncate">{title}</span>
        <span className="block text-xs themed-text-muted mt-0.5">{description}</span>
      </span>
    </label>
  )
}

interface Props {
  /** The selection. A single pointer names its files directly on the radios. */
  files: FileItem[]
  themeClass: string
  t: (key: string, options?: Record<string, unknown>) => string
  /** Either/or: choosing one is choosing NOT to have the other. */
  onConfirm: (choice: PointerDownloadChoice) => void | Promise<void>
  onCancel: () => void
}

/**
 * The download choice for a selection containing `.shared`/`.chain` pointers.
 *
 * A pointer row can yield two different files — the source content it points
 * at, and the pointer JSON — and until 2026-08-20 the context menu offered both
 * as peer "Download …" items, which read as two flavours of one action and left
 * "metadata" unexplained. There is one Download item now, and the choice lives
 * here: two radios, plus a single line saying what the second one is for.
 */
export function PointerDownloadDialog({ files, themeClass, t, onConfirm, onCancel }: Props) {
  const [choice, setChoice] = useState<PointerDownloadChoice>('contents')
  const [busy, setBusy] = useState(false)

  const plan = planPointerDownload(files)
  // One pointer names its own files on the radios; a selection lists what each
  // row produces above them.
  const single = files.length === 1 ? files[0] : null

  const confirm = async () => {
    if (busy) return
    setBusy(true)
    try {
      await onConfirm(choice)
    } finally {
      setBusy(false)
    }
  }

  // Same shape as `FolderLinkedDownloadDialog`: the heading is WHY the download
  // stopped to ask, not a restatement of what was clicked. "Download dev.shared"
  // named the row the user had just picked and left the reason unstated.
  const title = single
    ? t('driveView.pointerDownload.title', {
        defaultValue: 'This item is a {{ext}}',
        // The kind is KNOWN for one row, so name it. `.shared` is cross-tenant
        // and `.chain` is same-tenant; they are not interchangeable, and a
        // mixed selection therefore has to list both rather than pick one.
        ext: isChainPointerRow(single) ? '.chain' : '.shared',
      })
    : t('driveView.pointerDownload.titleMulti', {
        defaultValue: 'This selection contains .shared or .chain',
      })

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 animate-fade-in" onClick={busy ? undefined : onCancel} />

      {/* Named for assistive tech, like its sibling below. Without this the
          dialog announces as an unlabelled group and its heading -- the whole
          reason it interrupted -- is just the next thing read out. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative z-10 rounded-lg shadow-xl max-w-md w-full animate-scale-in ${themeClass} themed-bg-secondary`}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0 mt-0.5">
              <Download size={24} className="text-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold themed-text mb-2">{title}</h3>

              <p className="themed-text-secondary text-sm">
                {single
                  ? t('driveView.pointerDownload.bodySingle', {
                      defaultValue: 'How would you like to proceed with this pointer item?',
                    })
                  : t('driveView.pointerDownload.body', {
                      // "these pointer items" is plural and wrong for one row,
                      // which is why the single case has its own key rather than
                      // an i18next plural: a half-declared `_one`/`_other` pair
                      // renders the wrong branch silently.
                      defaultValue: 'How would you like to proceed with these pointer items?',
                    })}
              </p>

              {plan.shareFileCount > 0 && (
                <div className="mt-4 space-y-2" role="radiogroup">
                  {/* Either/or, not an extra: picking the pointer file means NOT
                      taking the contents. Contents is pre-selected because it is
                      the ordinary intent; the pointer file is deliberate. Each
                      card says what LANDS, so the two are distinguishable without
                      knowing what a `.shared` is. */}
                  <ChoiceCard
                    name="pointer-download-choice"
                    value="contents"
                    checked={choice === 'contents'}
                    onSelect={() => setChoice('contents')}
                    disabled={busy}
                    title={
                      !single
                        ? t('driveView.pointerDownload.optContentsMulti', { defaultValue: 'Download the content they point to' })
                        : single.isFolder
                          ? t('driveView.pointerDownload.optContentsZip', {
                              defaultValue: 'Download as ZIP ({{name}})', name: plan.producesFor(single),
                            })
                          : t('driveView.pointerDownload.optContentsFile', {
                              defaultValue: 'Download the file ({{name}})', name: plan.producesFor(single),
                            })
                    }
                    description={
                      !single
                        ? t('driveView.pointerDownload.optContentsMultiDesc', {
                            defaultValue: 'They become normal folders inside the zip, keeping their names.',
                          })
                        : single.isFolder
                          // Neutral about the primitive: a chain's target is not
                          // a "shared folder", and the distinction does not help
                          // here — what lands is the folder's contents either way.
                          ? t('driveView.pointerDownload.optContentsZipDesc', {
                              defaultValue: 'Everything inside the folder.',
                            })
                          : t('driveView.pointerDownload.optContentsFileDesc', {
                              defaultValue: 'The file this points to.',
                            })
                    }
                  />

                  {/* `.shared` is CROSS-tenant, `.chain` is SAME-tenant, and the
                      two are not interchangeable — different noun AND different
                      destination. A MIXED selection goes kind-neutral rather than
                      picking one primitive's noun and being wrong about the other. */}
                  <ChoiceCard
                    name="pointer-download-choice"
                    value="pointer"
                    checked={choice === 'pointer'}
                    onSelect={() => setChoice('pointer')}
                    disabled={busy}
                    title={
                      !single
                        ? t('driveView.pointerDownload.optPointerMulti', { defaultValue: 'Download as pointer' })
                        : isChainPointerRow(single)
                          ? t('driveView.pointerDownload.optChainFile', {
                              defaultValue: 'Download the chain file ({{name}})', name: single.name,
                            })
                          : t('driveView.pointerDownload.optShareFile', {
                              defaultValue: 'Download the share file ({{name}})', name: single.name,
                            })
                    }
                    description={
                      !single
                        ? t('driveView.pointerDownload.optPointerMultiDesc', {
                            defaultValue: 'They come as small files you can re-upload.',
                          })
                        : isChainPointerRow(single)
                          // A chain re-aligns ONLY in the drive it came from —
                          // the aligner rejects a foreign `targetTenantId`.
                          ? t('driveView.pointerDownload.optChainFileDesc', {
                              defaultValue: 'A small file that recreates this chain in this drive.',
                            })
                          : t('driveView.pointerDownload.optShareFileDesc', {
                              defaultValue: 'A small file that recreates this share in another drive.',
                            })
                    }
                  />
                </div>
              )}

              {/* One archive, so the useful fact is its NAME, not an
                  enumeration of what is inside it — and it sits BELOW the
                  choice, because it describes the outcome rather than asking
                  anything. A SINGLE row needs neither: its radios already name
                  both candidate files. */}
              {!single && plan.archiveName && (
                <p className="themed-text-muted text-xs mt-3">
                  {t('driveView.pointerDownload.oneArchive', {
                    defaultValue: '{{count}} items, saved as one zip: {{name}}',
                    count: plan.plain.length + plan.live.length + plan.shareFileOnly.length,
                    name: plan.archiveName,
                  })}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t themed-border">
          <button
            onClick={busy ? undefined : onCancel}
            disabled={busy}
            className="px-4 py-2 themed-btn-secondary rounded-lg text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-50"
          >
            {t('driveView.pointerDownload.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={confirm}
            disabled={busy}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-50"
          >
            {t('driveView.pointerDownload.confirm', { defaultValue: 'Download' })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export type FolderLinkedChoice = 'files' | 'contents'

/**
 * The choice offered when a folder being downloaded contains pointers.
 *
 * Either the pointers ride as their own `.shared`/`.chain` FILES — small, and
 * they re-upload and re-align as pointers — or `expandPointers` replaces each
 * with an ordinary folder holding what it points at, keeping the pointer's own
 * name. One archive either way.
 *
 * Every claim in the previous version of this comment was reversed by that
 * change and is recorded here because two of them still read as reasonable:
 *   - "their SOURCE contents cannot ride along … nesting would turn a pointer
 *     file into a directory, changing what the thing is" — that IS now what
 *     happens, deliberately, and nothing re-mounts on re-upload because a
 *     pointer is a row KIND that an uploaded directory does not have;
 *   - "necessarily the folder's zip PLUS one download per pointer, and the
 *     dialog says how many" — it is one zip, and the count is gone with it;
 *   - "reached only from its own menu item … a recursive server walk must not
 *     be charged to every download" — detection is automatic now, and that
 *     charge is real: it is what put a slow probe between the click and the
 *     save picker, which cost the click's transient activation on large
 *     folders. `hasLinkedPointers` caches for that reason.
 */
export function FolderLinkedDownloadDialog({
  folderName, folderCount = 1, themeClass, t, onConfirm, onCancel, defaultChoice = 'files', action = 'download',
}: {
  /** Not rendered: the heading says what the dialog is ABOUT, not which row was
   *  clicked. It names the dialog for assistive tech, where the surrounding
   *  "I just right-clicked test_2008" context does not exist. */
  folderName: string
  /** How many folders this download covers. The heading says "This folder" for
   *  one and "These folders" for several — a selection of three reaching a
   *  singular heading is the kind of small lie that makes a user distrust the
   *  rest of the dialog. */
  folderCount?: number
  themeClass: string
  t: (key: string, options?: Record<string, unknown>) => string
  onConfirm: (choice: FolderLinkedChoice) => void | Promise<void>
  onCancel: () => void
  /** The preselected answer. The drive keeps pointers ('files'); the share view
   *  preselects their CONTENTS (owner ruling 2026-09-27). */
  defaultChoice?: FolderLinkedChoice
  /** A zip (download) or a copy into a drive (copy / Save to Drive) — the SAME question,
   *  worded for what is about to happen. */
  action?: 'download' | 'copy'
}) {
  const [choice, setChoice] = useState<FolderLinkedChoice>(defaultChoice)
  const copy = action === 'copy'
  const [busy, setBusy] = useState(false)

  const confirm = async () => {
    if (busy) return
    setBusy(true)
    try { await onConfirm(choice) } finally { setBusy(false) }
  }

  // Computed ONCE. The accessible name is the heading plus which folders, and
  // when the two were derived separately the label kept saying "This folder"
  // about a heading reading "These folders".
  const heading = folderCount > 1
    ? t('driveView.linkedDownload.titleMany', { defaultValue: 'These folders contain .shared or .chain' })
    : t('driveView.linkedDownload.title', { defaultValue: 'This folder contains .shared or .chain' })

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 animate-fade-in" onClick={busy ? undefined : onCancel} />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${heading} — ${folderName}`}
        className={`relative z-10 rounded-lg shadow-xl max-w-md w-full animate-scale-in ${themeClass} themed-bg-secondary`}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0 mt-0.5">
              <Download size={24} className="text-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
              {/* The heading is WHY this interrupted the download, not what is
                  about to happen — "Download test_2008" named something the user
                  had just clicked and left the reason to a subtitle they had no
                  cue to read. It also carries the antecedent, so both options
                  can say "they" without re-naming the primitives. The extensions
                  are spelled out because they are what the user actually sees in
                  the drive. */}
              <h3 className="text-lg font-semibold themed-text mb-2">
                {heading}
              </h3>
              <p className="themed-text-secondary text-sm">
                {t('driveView.linkedDownload.body', {
                  defaultValue: 'How would you like to proceed with these pointer items?',
                })}
              </p>
              <div className="mt-4 space-y-2" role="radiogroup">
                <ChoiceCard
                  name="folder-linked-choice"
                  value="files"
                  checked={choice === 'files'}
                  onSelect={() => setChoice('files')}
                  disabled={busy}
                  title={copy
                    ? t('driveView.linkedCopy.optFiles', { defaultValue: 'Copy as pointer' })
                    : t('driveView.linkedDownload.optFiles', { defaultValue: 'Download as pointer' })}
                  description={copy
                    ? t('driveView.linkedCopy.optFilesDesc', { defaultValue: 'They stay .shared / .chain pointers — they open only where you have access.' })
                    : t('driveView.linkedDownload.optFilesDesc', {
                      defaultValue: 'The pointers come as small files you can re-upload.',
                    })}
                />
                <ChoiceCard
                  name="folder-linked-choice"
                  value="contents"
                  checked={choice === 'contents'}
                  onSelect={() => setChoice('contents')}
                  disabled={busy}
                  title={copy
                    ? t('driveView.linkedCopy.optContents', { defaultValue: 'Copy the content they point to' })
                    : t('driveView.linkedDownload.optContents', {
                      defaultValue: 'Download the content they point to',
                    })}
                  // The name is UNCHANGED, which is the reassuring half: a
                  // folder called `ddd.shared` re-uploads as a plain folder,
                  // because a pointer is a row KIND and a directory has neither
                  // half of it.
                  description={copy
                    ? t('driveView.linkedCopy.optContentsDesc', { defaultValue: 'They become normal folders, keeping their names.' })
                    : t('driveView.linkedDownload.optContentsDesc', {
                      defaultValue: 'They become normal folders inside the zip, keeping their names.',
                    })}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t themed-border">
          <button
            onClick={busy ? undefined : onCancel}
            disabled={busy}
            className="px-4 py-2 themed-btn-secondary rounded-lg text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-50"
          >
            {t('driveView.pointerDownload.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={confirm}
            disabled={busy}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-50"
          >
            {copy
              ? t('driveView.linkedCopy.confirm', { defaultValue: 'Copy' })
              : t('driveView.pointerDownload.confirm', { defaultValue: 'Download' })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
